// Mid-flow buttons in the digital checkin must NOT say "Submit". The only
// legitimate "Submit" button is the final one on ReviewAndSign — every other
// forward action should use "Save", "Continue", or a verb specific to the
// action (e.g. "Sign"). "Submit" mid-flow falsely signals the patient is done.
import React, { memo } from 'react';
import ScreenerSections from './ScreenerSections';
import type { ScreenerQuestionnaire } from '../types/screener';

export interface ScreenerFormItemProps {
  questionnaire: ScreenerQuestionnaire;
  isOpen: boolean;
  isSubmitted: boolean;
  isSubmitting: boolean;
  selectedAnswers: Record<string, string>;
  onToggleOpen: () => void;
  onAnswerChange: (key: string, value: string) => void;
  onSubmit: (templateId: number | string) => void;
}

const ScreenerFormItem = memo<ScreenerFormItemProps>(
  ({
    questionnaire,
    isOpen,
    isSubmitted,
    isSubmitting,
    selectedAnswers,
    onToggleOpen,
    onAnswerChange,
    onSubmit,
  }) => {
    const templateId = questionnaire.templateid;

    return (
      <div
        className={`border rounded-xl overflow-hidden ${
          isOpen ? 'border-[#01545D]' : 'border-[#A4BDC0]'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#DCE1E8] bg-white">
          <div className="flex items-center gap-3">
            <span className="text-base font-semibold text-[#1C1C1C]">
              {questionnaire.name}
            </span>
            {isSubmitted && (
              <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-bold bg-[#22C55E] text-white">
                Submitted
              </span>
            )}
          </div>
          <button
            type="button"
            className="text-sm font-bold text-[#01545D] cursor-pointer"
            onClick={onToggleOpen}
          >
            {isOpen ? 'Close' : 'Open'}
          </button>
        </div>

        {/* Expanded body */}
        {isOpen && (
          <div className="bg-[#F7F8FD] px-6 py-5 flex flex-col gap-6">
            <ScreenerSections
              sections={questionnaire.sections}
              templateId={templateId}
              selectedAnswers={selectedAnswers}
              onAnswerChange={onAnswerChange}
            />

            {!isSubmitted && (
              <button
                type="button"
                onClick={() => onSubmit(templateId)}
                disabled={isSubmitting}
                className="mt-2 px-4 py-2 bg-[#01545D] text-white rounded-md font-semibold text-sm hover:bg-[#0b7981] disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Saving...' : 'Save Screener'}
              </button>
            )}
          </div>
        )}
      </div>
    );
  },
  (prev, next) => {
    return (
      prev.questionnaire.templateid === next.questionnaire.templateid &&
      prev.questionnaire.name === next.questionnaire.name &&
      prev.isOpen === next.isOpen &&
      prev.isSubmitted === next.isSubmitted &&
      prev.isSubmitting === next.isSubmitting &&
      prev.selectedAnswers === next.selectedAnswers &&
      prev.onAnswerChange === next.onAnswerChange
    );
  },
);

ScreenerFormItem.displayName = 'ScreenerFormItem';

export default ScreenerFormItem;
